// ============================================
// Command Palette (⌘K / Ctrl+K): navegação, ações rápidas e busca de entidades.
// Montado no AppShell (shell autenticado). Abre com Cmd/Ctrl+K, fecha com Esc.
// Busca fuzzy simples (substring, case/acento-insensitive), navegação por
// teclado (↑↓ move, Enter executa), acessível (role=dialog + options).
// Com query digitada, também casa sensores (label/MAC) e unidades (nome,
// instalação e pai) via react-query — mesmas queryKeys das páginas, então o
// cache normalmente cobre.
// ============================================

import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import Dialog from '@mui/material/Dialog'
import Box from '@mui/material/Box'
import InputBase from '@mui/material/InputBase'
import List from '@mui/material/List'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import Typography from '@mui/material/Typography'
import SearchRoundedIcon from '@mui/icons-material/SearchRounded'
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded'
import Brightness4RoundedIcon from '@mui/icons-material/Brightness4Rounded'
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded'
import AddRoundedIcon from '@mui/icons-material/AddRounded'
import MemoryRoundedIcon from '@mui/icons-material/MemoryRounded'
import TvRoundedIcon from '@mui/icons-material/TvRounded'
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded'
import SensorsRoundedIcon from '@mui/icons-material/SensorsRounded'
import WarehouseRoundedIcon from '@mui/icons-material/WarehouseRounded'
import type { SvgIconComponent } from '@mui/icons-material'
import { useAuth } from '../auth/AuthContext'
import { useColorMode } from '../theme/ColorModeContext'
import { raizService } from '../services/raizService'
import { RAIZ_SECTIONS } from '../pages/Raiz/raizSections'
import { SETTINGS_SECTIONS, settingsPath } from '../pages/Raiz/settings/settingsSections'
import { deviceName, deviceSubName } from '../pages/Raiz/deviceName'

interface Command {
  id: string
  label: string
  /** Termos extra para casar na busca além do label. */
  keywords?: string
  /** Linha secundária (ex.: MAC do sensor) para desambiguar entidades homônimas. */
  hint?: string
  icon: SvgIconComponent
  group: 'Navegação' | 'Ações' | 'Sensores' | 'Unidades'
  run: () => void
}

/** Máximo de resultados por grupo de entidades — a paleta é atalho, não listagem. */
const ENTITY_LIMIT = 5

/** Casa `query` (normalizada) contra o alvo por substring, case/acento-insensitive. */
const normalize = (s: string): string =>
  s.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')

export default function CommandPalette (): JSX.Element {
  const navigate = useNavigate()
  const { isAdmin, isPlatformViewer, logout } = useAuth()
  const { toggle } = useColorMode()

  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [active, setActive] = useState(0)
  const inputRef = useRef<HTMLInputElement>(null)
  const listRef = useRef<HTMLUListElement>(null)

  // Atalho global: ⌘K / Ctrl+K alterna a paleta (funciona mesmo com foco em input).
  // O evento 'raiz:open-palette' permite abrir por clique (chip do topbar).
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault()
        setOpen((o) => !o)
      }
    }
    const onOpenEvent = (): void => setOpen(true)
    window.addEventListener('keydown', onKey)
    window.addEventListener('raiz:open-palette', onOpenEvent)
    return () => {
      window.removeEventListener('keydown', onKey)
      window.removeEventListener('raiz:open-palette', onOpenEvent)
    }
  }, [])

  const close = (): void => setOpen(false)

  // Entidades pesquisáveis (sensores/unidades): mesmas queryKeys das páginas —
  // o cache normalmente já cobre; staleTime evita refetch a cada abertura.
  const devicesQ = useQuery({
    queryKey: ['raiz', 'devices'],
    queryFn: raizService.getDevices,
    staleTime: 30_000,
    enabled: open,
  })
  const unitsQ = useQuery({
    queryKey: ['raiz', 'units'],
    queryFn: raizService.listUnits,
    staleTime: 30_000,
    enabled: open,
  })

  // Ao fechar, zera busca/seleção para a próxima abertura começar limpa.
  useEffect(() => {
    if (!open) {
      setQuery('')
      setActive(0)
    }
  }, [open])

  const commands = useMemo<Command[]>(() => {
    const nav: Command[] = RAIZ_SECTIONS
      .filter((s) => (!s.adminOnly || isAdmin) && (!s.platformOnly || isPlatformViewer))
      .map((s) => ({
        id: `nav:${s.path}`,
        label: `Ir para ${s.label}`,
        keywords: s.label,
        icon: ArrowForwardRoundedIcon,
        group: 'Navegação' as const,
        run: () => navigate(s.path),
      }))
    // Sub-rotas de Configurações (#131): "Usuários" ganha atalho direto; o hub
    // continua acima como "Ir para Configurações". adminOnly como a seção.
    if (isAdmin) {
      for (const s of SETTINGS_SECTIONS) {
        nav.push({
          id: `nav:${settingsPath(s.slug)}`,
          label: `Ir para Configurações › ${s.label}`,
          keywords: `${s.label} ${s.description} configurações`,
          icon: ArrowForwardRoundedIcon,
          group: 'Navegação',
          run: () => navigate(settingsPath(s.slug)),
        })
      }
    }

    const actions: Command[] = [
      {
        id: 'action:toggle-theme',
        label: 'Alternar tema',
        keywords: 'claro escuro dark light modo cor',
        icon: Brightness4RoundedIcon,
        group: 'Ações',
        run: () => toggle(),
      },
      {
        id: 'action:excursions',
        label: 'Ver excursões abertas',
        keywords: 'alertas alarmes temperatura',
        icon: WarningAmberRoundedIcon,
        group: 'Ações',
        run: () => navigate('/events/excursions'),
      },
      {
        id: 'action:warnings',
        label: 'Ver avisos dos sensores',
        keywords: 'eventos bateria baixa offline hibernando sensor mudo atrasado calibração',
        icon: WarningAmberRoundedIcon,
        group: 'Ações',
        run: () => navigate('/events/warnings'),
      },
      {
        id: 'action:new-unit',
        label: 'Nova unidade',
        keywords: 'cadastrar criar câmara caminhão',
        icon: AddRoundedIcon,
        group: 'Ações',
        run: () => navigate('/units?new=1'),
      },
      {
        id: 'action:tv-mode',
        label: 'Modo TV',
        keywords: 'painel parede mural tela cheia wallboard monitor',
        icon: TvRoundedIcon,
        group: 'Ações',
        run: () => navigate('/tv'),
      },
    ]

    if (isAdmin) {
      actions.push({
        id: 'action:new-device',
        label: 'Cadastrar device',
        keywords: 'sensor chave key gestão',
        icon: MemoryRoundedIcon,
        group: 'Ações',
        run: () => navigate('/device-keys'),
      })
    }

    actions.push({
      id: 'action:logout',
      label: 'Sair',
      keywords: 'logout sessão encerrar',
      icon: LogoutRoundedIcon,
      group: 'Ações',
      run: () => { void logout() },
    })

    return [...nav, ...actions]
  }, [isAdmin, isPlatformViewer, navigate, toggle, logout])

  // Grupos "Sensores"/"Unidades": só com query digitada (não poluem o estado inicial).
  const entityCommands = useMemo<Command[]>(() => {
    const q = normalize(query.trim())
    if (q === '') return []
    const sensores: Command[] = (devicesQ.data?.devices ?? [])
      .filter((d) => normalize(`${d.display_name ?? ''} ${d.label ?? ''} ${d.mac}`).includes(q))
      .slice(0, ENTITY_LIMIT)
      .map((d) => ({
        id: `device:${d.mac}`,
        label: deviceName(d),
        // deviceSubName devolve o MAC só quando o primário NÃO é o MAC — antes
        // isto olhava só o label, então sensor com display_name e sem label perdia o hint.
        hint: deviceSubName(d) ?? undefined,
        icon: SensorsRoundedIcon,
        group: 'Sensores' as const,
        run: () => navigate(`/${encodeURIComponent(d.mac)}`),
      }))
    // Unidade casa também pela instalação e pelo pai: com vários "Freezer 2"
    // em salas e sites diferentes, o nome sozinho não distingue, e quem busca
    // "cold lab" quer achar o que está dentro dela. O hint repete o caminho
    // (BSB/LOG › Cold Lab) para desambiguar homônimos na lista.
    const unidades: Command[] = (unitsQ.data ?? [])
      .filter((u) => normalize(`${u.name} ${u.site_code ?? ''} ${u.parent_name ?? ''}`).includes(q))
      .slice(0, ENTITY_LIMIT)
      .map((u) => ({
        id: `unit:${u.id}`,
        label: u.name,
        hint: [u.site_code, u.parent_name].filter(Boolean).join(' › ') || undefined,
        icon: WarehouseRoundedIcon,
        group: 'Unidades' as const,
        run: () => navigate(`/units?selected=${u.id}`),
      }))
    return [...sensores, ...unidades]
  }, [query, devicesQ.data, unitsQ.data, navigate])

  const filtered = useMemo<Command[]>(() => {
    const q = normalize(query.trim())
    if (q === '') return commands
    return [
      ...commands.filter((c) => normalize(`${c.label} ${c.keywords ?? ''}`).includes(q)),
      ...entityCommands,
    ]
  }, [commands, entityCommands, query])

  // Mantém o índice ativo dentro dos limites quando a lista filtrada encolhe.
  useEffect(() => {
    setActive((a) => (a >= filtered.length ? 0 : a))
  }, [filtered.length])

  const execute = (cmd: Command | undefined): void => {
    if (!cmd) return
    close()
    cmd.run()
  }

  const onInputKeyDown = (e: React.KeyboardEvent): void => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setActive((a) => (filtered.length === 0 ? 0 : (a + 1) % filtered.length))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActive((a) => (filtered.length === 0 ? 0 : (a - 1 + filtered.length) % filtered.length))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      execute(filtered[active])
    }
    // Esc é tratado pelo onClose do Dialog.
  }

  // Rola o item ativo para dentro da vista ao navegar pelo teclado.
  // (scrollIntoView pode não existir no jsdom — guardamos por segurança.)
  useEffect(() => {
    const el = listRef.current?.querySelector<HTMLElement>(`[data-index="${active}"]`)
    el?.scrollIntoView?.({ block: 'nearest' })
  }, [active])

  const listboxId = 'command-palette-listbox'

  return (
    <Dialog
      open={open}
      onClose={close}
      fullWidth
      maxWidth="sm"
      // Ancorar no topo lembra as paletas de comando conhecidas (Spotlight/VS Code).
      sx={{ '& .MuiDialog-container': { alignItems: 'flex-start' } }}
      PaperProps={{
        sx: { mt: '12vh', borderRadius: 3, overflow: 'hidden' },
        role: 'dialog',
        'aria-label': 'Paleta de comandos',
      }}
      // TransitionProps garante foco no input assim que a animação de abertura termina.
      TransitionProps={{ onEntered: () => inputRef.current?.focus() }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
        <SearchRoundedIcon sx={{ color: 'text.secondary' }} />
        <InputBase
          inputRef={inputRef}
          value={query}
          onChange={(e) => { setQuery(e.target.value); setActive(0) }}
          onKeyDown={onInputKeyDown}
          placeholder="Buscar seções, ações, sensores e unidades…"
          fullWidth
          inputProps={{
            'aria-label': 'Buscar seções, ações, sensores e unidades',
            'aria-controls': listboxId,
            'aria-activedescendant': filtered[active] ? `cmd-${filtered[active].id}` : undefined,
            role: 'combobox',
            'aria-expanded': true,
            'aria-autocomplete': 'list',
          }}
          sx={{ fontSize: 16 }}
        />
      </Box>

      {filtered.length === 0 ? (
        <Box sx={{ px: 2, py: 4, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Nenhum comando encontrado.
          </Typography>
        </Box>
      ) : (
        <List
          ref={listRef}
          id={listboxId}
          role="listbox"
          aria-label="Comandos"
          dense
          sx={{ maxHeight: '50vh', overflowY: 'auto', py: 0.5 }}
        >
          {filtered.map((cmd, i) => {
            const prev = filtered[i - 1]
            const showHeader = !prev || prev.group !== cmd.group
            const Icon = cmd.icon
            const selected = i === active
            return (
              <Box key={cmd.id}>
                {showHeader && (
                  <Typography
                    variant="overline"
                    sx={{ display: 'block', px: 2, pt: i === 0 ? 0.5 : 1.5, pb: 0.25, color: 'text.secondary' }}
                  >
                    {cmd.group}
                  </Typography>
                )}
                <ListItemButton
                  id={`cmd-${cmd.id}`}
                  data-index={i}
                  role="option"
                  aria-selected={selected}
                  selected={selected}
                  onMouseEnter={() => setActive(i)}
                  onClick={() => execute(cmd)}
                  sx={{ px: 2, borderRadius: 1.5, mx: 1 }}
                >
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <Icon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary={cmd.label} secondary={cmd.hint} />
                </ListItemButton>
              </Box>
            )
          })}
        </List>
      )}
    </Dialog>
  )
}
